import { BorderBoxWrapper, BorderBoxWrapperProps } from 'components/common/BorderBoxWrapper'
import { FC, ReactNode } from 'react'

import { BEIGE_600, BLUE_300, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { IconButton } from '@mui/material'
import { MUIDivider } from 'components/common/MUIDivider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

/** Props for the SectionedBorderBox component. */
interface Props extends Omit<BorderBoxWrapperProps, 'title'> { // For some reason I get an error if I don't use Omit here
  /** The main title displayed in the sectioned border box. If you need to display button on the right side of header, use title as ReactNode and pass the button as another children with styling. */
  title: string | ReactNode
  /** An optional description displayed below the main title. */
  description?: string
  /** OnIconClick function to be called */
  onIconClick?: () => void
  /** Custom icon displaying on the right side of header. Binding with function onIconClick. */
  customIcon?: ReactNode
  /** Custom button displaying on the right side of header. */
  children: ReactNode
  /** `flat`: beige border, no shadow, a rule under the title, and a ring while a choice control inside has
   *  keyboard focus (not a text field). */
  variant?: 'sectioned' | 'flat'
  /** `flat` only: drops the rule under the title and centres it. */
  centeredTitle?: boolean
}

/**
 * @component
 * SectionedBorderBox component provides a styled border box with a title, optional description,
 * and content section separated by a divider.
 * 
 * If you need to display button on the right side of header, use title as ReactNode and pass the button as another children with styling.
 *
 * @example
 * <SectionedBorderBox
 *   title="Dunder Mifflin Infinity"
 *   description="The best paper company in Scranton."
 *   boxProps={{ borderColor: BLUE_600 }}
 * >
 *   <SectionContent />
 * </SectionedBorderBox>
 */
export const SectionedBorderBox: FC<Props> = ({
  title,
  description,
  children,
  onIconClick,
  customIcon,
  variant = 'sectioned',
  centeredTitle = false,
  ...boxProps
}) => {
  const isFlat = variant === 'flat'
  const flatProps = isFlat
    ? {
      borderColor: BEIGE_600,
      borderRadius: '1.2rem',
      elevation: 'none' as const,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 1.6,
      sx: {
        transition: 'box-shadow 120ms ease',
        '&:has(:focus-visible)': { boxShadow: `0 0 0 3px ${BLUE_300}` },
        // The ring marks a choice being made; a text field shows its own focus.
        '&:has(:is(input:not([type="radio"], [type="checkbox"]), textarea, [role="combobox"]):focus)': { boxShadow: 'none' },
      },
    }
    : {}

  return (
    <BorderBoxWrapper padding={2} {...flatProps} {...boxProps}>

      {/** HEADER */}
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        sx={isFlat && !centeredTitle ? { borderBottom: `1px solid ${BEIGE_600}`, pb: 1.2 } : undefined}
      >

        <Stack gap={0.5} width="100%" textAlign={centeredTitle ? 'center' : undefined}>
          {typeof title === 'string'
            ? <Typography color={GRAY_900} variant='text-lg' fontWeight={600}>{title}</Typography>
            : title // ReactNode
          }
          {description && <Typography variant="text-sm" color={isFlat ? GRAY_700 : undefined}>{description}</Typography>}
        </Stack>

        {onIconClick &&
          <IconButton
            sx={{ width: '3rem', height: '3rem' }}
            onClick={onIconClick}
          >
            {customIcon}
          </IconButton>}
      </Stack>

      {!isFlat && <MUIDivider margin={12} />}

      {/** CONTENT */}
      {children}

    </BorderBoxWrapper>
  )
}
